import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { PayoutSupport } from '../business-report/_types';
import { denormalize } from '../shared/normalizers';
import { SessionUser } from '../user/_types';

import {
  Balance,
  HideWorkoutSchedules,
  InviteLink,
  Platform,
  RequireLocationResponse,
  TenDlcDetails,
  Transfer,
} from './_types';
import {
  denormalizePlatform,
  normalizeInviteLink,
  normalizePlatform,
} from './normalizers';

export const platformsQueryKey = 'platforms';

interface FetchPlatformOptions {
  id: Platform['id'];
}

export const fetchPlatform = async ({
  id,
}: FetchPlatformOptions): Promise<Platform> => {
  const response = await api.get(`/api/v5/platforms/${id}`);
  return normalizePlatform(response.data);
};

// Platform Stats interface
export interface PlatformStats {
  customer_info: {
    clients: number;
    purchasers: number;
    group_members: number;
    active_users: number;
  };
}

export const fetchPlatformStats = async ({
  id,
}: FetchPlatformOptions): Promise<PlatformStats> => {
  const response = await api.get(`/api/v5/platforms/${id}/stats/`);
  return response.data;
};

interface FetchStripeAccountsOptions {
  id: string;
}

export const fetchStripeAccounts = async ({
  id: platformId,
}: FetchStripeAccountsOptions) => {
  const response = await api.get(
    `/api/v2/stripe_accounts?platform_id=${platformId}`
  );

  return response.data?.data;
};

interface PlatformFilterOptions {
  search?: string;
  active?: string;
  ios?: {
    min?: string;
    max?: string;
  };
  android?: {
    min?: string;
    max?: string;
  };
  createdAt?: {
    start?: string;
    end?: string;
  };
  twilio10dlcStatus?: string;
  cloudflareStatus?: string;
  featureFlags?: { values: string[] };
}

interface FetchPlatformsOptions {
  page?: number;
  perPage?: number;
  filters?: PlatformFilterOptions;
  sortBy?: string;
  sortDir?: string;
  fetchAll?: boolean;
  siteIds?: string[];
}

interface FetchPlatformsResponse {
  platforms: Platform[];
  total: number;
}

export const fetchAllPlatforms = async (): Promise<Platform[]> => {
  const response = await api.get('/api/v5/platforms/', {
    params: {
      fetch_all: true,
    },
  });

  const data = response.data;

  return data.map((p) => normalizePlatform(p));
};

// Helper function to build platform query parameters
const buildPlatformQueryParams = (
  filters?: PlatformFilterOptions,
  sortBy?: string,
  sortDir?: string
) => {
  return {
    s: sortBy && sortDir ? `${sortBy} ${sortDir}` : 'created_at desc',
    ...(filters?.search
      ? {
          brand_or_trainer_formatted_name_or_trainer_email_cont: filters.search,
        }
      : {}),
    ...(filters?.active
      ? filters.active === 'active'
        ? { active: true }
        : { cancelled: true }
      : {}),
    ...(filters?.ios?.min ? { current_ios_version_gteq: filters.ios.min } : {}),
    ...(filters?.ios?.max ? { current_ios_version_lteq: filters.ios.max } : {}),
    ...(filters?.android?.min
      ? { current_android_version_gteq: filters.android.min }
      : {}),
    ...(filters?.android?.max
      ? { current_android_version_lteq: filters.android.max }
      : {}),
    ...(filters?.createdAt?.start
      ? { created_at_gteq: filters.createdAt.start }
      : {}),
    ...(filters?.createdAt?.end
      ? { created_at_lteq: filters.createdAt.end }
      : {}),
    ...(filters?.twilio10dlcStatus
      ? { twilio_10dlc_status_eq: filters.twilio10dlcStatus }
      : {}),
    ...(filters?.cloudflareStatus
      ? { cloudflare_status_eq: filters.cloudflareStatus }
      : {}),
    ...(filters?.featureFlags?.values?.length
      ? { with_feature_flags: filters.featureFlags.values.join(',') }
      : {}),
  };
};

export const fetchPlatforms = async ({
  page = 1,
  perPage = 20,
  filters,
  sortBy,
  sortDir,
  fetchAll,
  siteIds,
}: FetchPlatformsOptions): Promise<FetchPlatformsResponse> => {
  const response = await api.get(`/api/v5/platforms/`, {
    params: {
      ...(fetchAll
        ? { fetch_all: true }
        : {
            page,
            per: perPage,
          }),
      q: buildPlatformQueryParams(filters, sortBy, sortDir),
      ...(siteIds ? { site_ids: siteIds } : {}),
    },
  });

  if (fetchAll) {
    return {
      platforms: response.data.map((p) => normalizePlatform(p)),
      total: response.data.length,
    };
  } else {
    return {
      platforms: response.data.platform.map((p) => normalizePlatform(p)),
      total: response.data.meta.total,
    };
  }
};

export const fetchPlatformsCsv = ({
  filters,
  sortBy,
  sortDir,
}: Omit<FetchPlatformsOptions, 'page' | 'perPage' | 'fetchAll'>): void => {
  const baseUrl = '/api/v2/platforms.csv';
  const queryParams = new URLSearchParams();

  // Always fetch all for CSV export (same structure as fetchPlatforms)
  queryParams.append('fetch_all', 'true');

  // Build the query parameters using the same logic as fetchPlatforms
  const qParams = buildPlatformQueryParams(filters, sortBy, sortDir);

  // Add each q parameter to the URL
  Object.entries(qParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(`q[${key}]`, String(value));
    }
  });

  // Create full URL and trigger download
  const fullUrl = `${baseUrl}?${queryParams.toString()}`;
  window.open(fullUrl, '_blank');
};

export const fetchPlatformSmsDetails = async ({
  id,
}: FetchPlatformOptions): Promise<TenDlcDetails> => {
  const response = await api.get(`/api/v5/platforms/${id}/sms_details`);
  return camelcaseKeys(response.data);
};

export const updatePlatform = async (
  platformId: Platform['id'],
  fields: Partial<Platform>,
  wrapInPlatformObject?: boolean,
  user?: SessionUser | null
): Promise<Platform> => {
  const data = denormalizePlatform(fields, user);
  const response = await api.put(
    `/api/v5/platforms/${platformId}`,
    wrapInPlatformObject ? { platform: data } : data,
    {}
  );
  return normalizePlatform(response.data);
};

export const updatePlatformRoles = async (
  platformId: Platform['id'],
  roles: Platform['platformRoles']
): Promise<Platform> => {
  const data = denormalizePlatform({ platformRoles: roles }) as any;
  const response = await api.put(`/api/v5/platforms/${platformId}`, {
    platform: {
      platform_roles: data.platform_roles,
    },
  });
  return normalizePlatform(response.data);
};

export interface ApplyRoleToTrainersResponse {
  success: boolean;
  assigned_trainer_count: number;
}

/**
 * Explicitly propagate a subset of a platform role's permissions onto every
 * trainer currently assigned that role. Only the fields listed in
 * `changedFields` are overwritten; per-trainer customizations in any other
 * field are preserved. See EXCOM-2485.
 */
export const applyRoleToTrainers = async (
  platformId: Platform['id'],
  roleId: string,
  changedFields: string[]
): Promise<ApplyRoleToTrainersResponse> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/apply_role_to_trainers`,
    {
      role_id: roleId,
      changed_fields: changedFields,
    }
  );
  return response.data;
};

export const deletePlatform = async (
  platformId: Platform['id']
): Promise<void> => {
  await api.delete(`/api/v3/platforms/${platformId}`);
};

export const updatePlatformSmsDetails = async (
  platformId: Platform['id'],
  row: string
): Promise<TenDlcDetails> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/initiate_10_dlc`,
    { csv_data: row }
  );
  return camelcaseKeys(response.data);
};

export const submitPlatformSmsCampaign = async (
  platformId: Platform['id']
): Promise<TenDlcDetails> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/submit_10_dlc_campaign`
  );
  return camelcaseKeys(response.data);
};

export const submitPlatformSmsPhoneNumber = async (
  platformId: Platform['id']
): Promise<TenDlcDetails> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/submit_10_dlc_phone_number`
  );
  return camelcaseKeys(response.data);
};

export const finalizePlatform10Dlc = async (
  platformId: Platform['id']
): Promise<TenDlcDetails> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/finalize_10_dlc`
  );
  return camelcaseKeys(response.data);
};

export const resetPlatform10Dlc = async (
  platformId: Platform['id'],
  action?: 'include' | 'exclude'
): Promise<TenDlcDetails> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/reset_10_dlc`,
    { dlc_action: action }
  );
  return camelcaseKeys(response.data);
};

export const clearPlatform10DlcError = async (
  platformId: Platform['id']
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/clear_10dlc_error`
  );
  return response.data;
};

export const resendPlatformOtp = async (
  platformId: Platform['id']
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/api/v5/platforms/${platformId}/resend_otp`);
  return response.data;
};

export const fetchPlatformAppVersions = async (): Promise<{
  ios: string[];
  android: string[];
}> => {
  const response = await api.get('/api/v5/platforms/app_versions');
  return response.data;
};

// Admin calls (not necessarily single platform specific)
export const updateTenDlcRegisteredNumbers = async (
  numbers: string
): Promise<string> => {
  const response = await api.post(
    `/api/v5/platforms/update_10_dlc_registered_numbers`,
    {
      numbers,
    }
  );

  return response.data;
};

export const triggerTwilio10DlcProcessing = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await api.post(`/api/v5/platforms/trigger_10dlc_processing`);

  return response.data;
};

interface FetchPlatformOptionsResponse {
  id: Identifier;
  brand: string;
  trainerId: Identifier;
}

export const fetchPlatformOptions = async (
  params: any = {}
): Promise<FetchPlatformOptionsResponse[]> => {
  const response = await api.get('/api/v2/platforms', {
    params: {
      pluck_columns: 'id,trainer_id,users.first_name,users.last_name,brand',
      fetch_all: true,
      ...params,
    },
  });

  return response.data
    .map((platform) => ({
      id: platform.id,
      brand:
        platform.brand ||
        `${platform.first_name || ''} ${platform.last_name || ''}`,
      trainerId: platform.trainer_id,
    }))
    .sort((a, b) => a.brand.localeCompare(b.brand));
};

export const transferPlatform = async ({
  platformId,
  userIds,
  siteId,
}: {
  platformId: Platform['id'];
  userIds: Identifier[];
  siteId: Identifier;
}): Promise<void> => {
  await api.post(`/api/v5/platforms/${platformId}/transfer`, {
    user_ids: userIds,
    platform_id: siteId,
  });
};

export const fetchBalance = async (): Promise<Balance> => {
  const response = await api.get('/api/v5/platforms/balance');
  return response.data;
};

export const fetchTransfers = async (): Promise<Transfer[]> => {
  const response = await api.get('/api/v5/platforms/transfers');
  return response.data;
};

export const fetchPayoutSupport = async (): Promise<PayoutSupport> => {
  const response = await api.get('/api/v5/platforms/supports_payouts');
  return camelcaseKeys(response.data) as PayoutSupport;
};

export const updateAllAccountTimezones = async (
  platformId: Platform['id']
): Promise<void> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/update_timezone`
  );
  return response.data;
};

export const removeDefaultExercises = async (
  platformId: Platform['id']
): Promise<void> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/remove_default_exercises`
  );
  return response.data;
};

export const refreshVimeoStreamUrl = async (
  platformId: Platform['id']
): Promise<void> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/refresh_vimeo_stream_url`
  );
  return response.data;
};

export const importRusinVideos = async (
  platformId: Platform['id']
): Promise<void> => {
  const response = await api.post(
    `/api/v2/platforms/${platformId}/import_rusin_videos`
  );
  return response.data;
};

export interface UpdatePayoutScheduleParams {
  interval: 'manual' | 'weekly' | 'monthly';
  weeklyAnchor?: string;
  monthlyAnchor?: number;
  location?: string;
}

interface UpdatePayoutScheduleResponse {
  success: boolean;
}

export const updatePayoutSchedule = async ({
  interval,
  weeklyAnchor,
  monthlyAnchor,
  location,
}: UpdatePayoutScheduleParams): Promise<UpdatePayoutScheduleResponse> => {
  const payload = {
    schedule: {
      interval,
      location,
    },
  };

  if (interval === 'weekly' && weeklyAnchor) {
    payload['schedule']['weekly_anchor'] = weeklyAnchor.toLowerCase();
  }

  if (interval === 'monthly' && monthlyAnchor) {
    payload['schedule']['monthly_anchor'] = monthlyAnchor;
  }

  const response = await api.post('/api/v5/platforms/update_payouts', payload);
  return response.data;
};

interface InstantPayoutParams {
  amount: number;
}

interface InstantPayoutResponse {
  status: string;
}

export const createInstantPayout = async ({
  amount,
}: InstantPayoutParams): Promise<InstantPayoutResponse> => {
  const response = await api.post('/api/v5/platforms/instant_payout', {
    amount,
  });
  return response.data;
};

/**
 * Sends the app download link text message to the user.
 */
export const sendAppDownloadLink = async ({
  phoneNumber,
  countryCode,
}: {
  phoneNumber: string;
  countryCode: string;
}): Promise<void> => {
  await api.post('/exercisecom/user_plans/send_text.js', {
    phone_number: phoneNumber,
    phone_number_country: countryCode,
  });
};

/**
 * Sets the user preference to hide the download app banner.
 */
export const hideAppDownloadBannerPreference = async (): Promise<void> => {
  await api.post('/exercisecom/user_plans/send_text.js', { hide_banner: true });
};

export type AppBuild = {
  name: string;
  url: string;
  size: number;
  updated_at: string;
};

export type AppBuildsResponse = {
  android_builds: AppBuild[];
  ios_builds: AppBuild[];
};

export const fetchPlatformAppBuilds = async (
  platformId: Platform['id']
): Promise<AppBuildsResponse> => {
  const response = await api.get(`/api/v5/platforms/${platformId}/app_builds`);
  return response.data;
};

export const fetchWorkoutPlanTags = async (): Promise<
  {
    name: string;
    id: string;
    description: string;
    tagType: string;
    collectionImageUrl: string;
  }[]
> => {
  const response = await api.get('/api/v4/tags', {
    params: {
      fetch_all: true,
      type: 'workout-plan,difficulty,equipment,badge,workout-time,frequency,objective,alternate-video',
    },
  });
  return response.data.map(camelcaseKeys);
};

export const createSiteInviteLink = async (
  nodeId: Identifier
): Promise<InviteLink> => {
  const response = await api.post(`/api/v4/invite_links/site`, {
    node_id: nodeId,
  });
  return normalizeInviteLink(response.data);
};

export const fetchSiteInviteLinks = async (
  itemId: Identifier,
  itemType: string,
  notExpired?: boolean,
  per?: number
): Promise<{ inviteLink: InviteLink[]; total: number }> => {
  const response = await api.get(`/api/v4/invite_links/`, {
    params: {
      q: {
        invite_item_id_eq: itemId,
        invite_item_type_eq: itemType,
      },
      s: [{ name: 'created_at', dir: 'desc' }],
      ...(notExpired ? { not_expired: true } : {}),
      per: per,
    },
  });
  return {
    inviteLink: response.data.invite_link.map(normalizeInviteLink),
    total: response.data?.meta?.total || response.data.invite_link.length,
  };
};

interface StripeAccountSessionParams {
  taxSettings?: boolean;
  taxRegistrations?: boolean;
  productTaxCodeSelector?: boolean;
  taxThresholdMonitoring?: boolean;
  exportTaxTransactions?: boolean;
}

interface StripeAccountSessionResponse {
  clientSecret: string;
}

export const fetchStripeAccountSession = async (
  params: StripeAccountSessionParams = {}
): Promise<StripeAccountSessionResponse> => {
  const response = await api.get('/api/v5/platforms/stripe_account_session', {
    params: {
      ...(params.taxSettings && { tax_settings: true }),
      ...(params.taxRegistrations && { tax_registrations: true }),
      ...(params.productTaxCodeSelector && { product_tax_code_selector: true }),
      ...(params.taxThresholdMonitoring && { tax_threshold_monitoring: true }),
      ...(params.exportTaxTransactions && { export_tax_transactions: true }),
    },
  });
  return {
    clientSecret: response.data.client_secret,
  };
};

export const updatePlatformMetric = async (
  platformId: Platform['id'],
  metric: boolean
): Promise<void> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/update_platform_metric/`,
    {
      metric,
    }
  );
  return response.data;
};

export const fetchHideWorkoutSchedules = async (): Promise<
  HideWorkoutSchedules[]
> => {
  const response = await api.get('/api/v3/hide_workout_schedules', {
    params: {
      fetch_all: true,
      q: {
        s: 'start_date asc',
      },
    },
  });
  return response.data.map(camelcaseKeys);
};

export const createHideWorkoutSchedules = async (
  hideWorkoutSchedules: Partial<HideWorkoutSchedules>
): Promise<HideWorkoutSchedules> => {
  const response = await api.post('/api/v3/hide_workout_schedules', {
    hide_workout_schedule: denormalize(hideWorkoutSchedules),
  });
  return camelcaseKeys(response.data);
};

export const updateHideWorkoutSchedules = async (
  id: HideWorkoutSchedules['id'],
  hideWorkoutSchedules: Partial<HideWorkoutSchedules>
): Promise<HideWorkoutSchedules> => {
  const response = await api.put(`/api/v3/hide_workout_schedules/${id}`, {
    hide_workout_schedule: denormalize(hideWorkoutSchedules),
  });
  return camelcaseKeys(response.data);
};

export const deleteHideWorkoutSchedules = async (
  id: HideWorkoutSchedules['id']
): Promise<HideWorkoutSchedules> => {
  const response = await api.delete(`/api/v3/hide_workout_schedules/${id}`);
  return camelcaseKeys(response.data);
};

export const resignWaiver = async (
  platformId: Platform['id']
): Promise<HideWorkoutSchedules> => {
  const response = await api.post(
    `/api/v5/platforms/${platformId}/resign_waiver/`
  );
  return camelcaseKeys(response.data);
};

export const republishPlatform = async (
  platformId: Platform['id']
): Promise<Platform> => {
  const response = await api.put(`/api/v5/platforms/${platformId}/republish`);
  return normalizePlatform(response.data);
};

export const updateIosPushCredentials = async (
  platformId: Platform['id']
): Promise<void> => {
  const response = await api.post(
    `/api/v2/platforms/${platformId}/update_ios_push_credentials`
  );
  return response.data;
};

export const syncAccessControlGroups = async (platformId: Platform['id']) => {
  return await api.get(
    `/api/v5/platforms/${platformId}/sync_access_control_groups`
  );
};

/**
 * Fetches whether the platform requires location selection for checkout.
 * Returns true if any product has inventory tracking enabled.
 */
export const fetchPlatformRequireLocation =
  async (): Promise<RequireLocationResponse> => {
    const response = await api.get('/api/v4/platforms/require_location');
    return camelcaseKeys(response.data, { deep: true }) as RequireLocationResponse;
  };
